import { compareDesc, parse } from 'date-fns'
import { Service } from '@tino-frontend/adapters'

import { ClientList, ClientListKycStatus, ClientListTermsStatus } from '@types'

export enum ClientListServiceStatus {
  Register = 'Registered',
  Terms = 'terms_accepted',
  Credit = 'credit_approved',
}

export type GetClientsArgs = {
  offset?: number
  limit?: number
  status?: ClientListServiceStatus[]
  search?: string
}

type ClientDTO = {
  id: string
  document_number: string
  legal_name: string
  fantasy_name: string
  created_at: string
  limit: number | null
  available_limit: number | null
  is_limit_blocked?: boolean
  term_status: 'pending' | 'requested' | 'approved' | 'rejected'
  kyc_status: 'pending' | 'approved' | 'rejected'
}

type ServiceResponse = { merchants: ClientDTO[] }

const DEFAULT_LIMIT = 10

const DATE_FORMAT = 'dd/MM/yyyy'

export class ClientListService {
  constructor(private readonly supplierBff: Service) {}

  private async getData(supplierId: string): Promise<ClientList[]> {
    const url = `v2/merchants?supplier_id=${supplierId}`
    const response = await this.supplierBff.get<
      ServiceResponse,
      ServiceResponse
    >(url)
    return this.mapData(response?.merchants)
  }

  private mapData(data: ClientDTO[]): ClientList[] {
    return data.map(
      (client) =>
        new ClientList({
          available_limit: client.available_limit,
          is_limit_blocked: client.is_limit_blocked,
          company_id: client.id,
          company_name: client.legal_name,
          company_document_number: client.document_number,
          register_date: client.created_at,
          terms_status: client.term_status as ClientListTermsStatus,
          kyc_status: client.kyc_status as ClientListKycStatus,
          limit: client.limit,
        })
    )
  }

  public sortByDate(data: ClientList[]) {
    return data.sort((a, b) =>
      compareDesc(
        parse(a.register_date, DATE_FORMAT, new Date()),
        parse(b.register_date, DATE_FORMAT, new Date())
      )
    )
  }

  public sortByName(data: ClientList[]) {
    return data.sort((a, b) =>
      a.company_name?.localeCompare(b.company_name ?? '')
    )
  }

  private search(list: ClientList[], query: string): ClientList[] {
    const searchTerm = query.toLowerCase()

    return list.filter(({ company_name, company_document_number }) => {
      return (
        this.searchByName(company_name, searchTerm) ||
        this.searchByDocumentNumber(company_document_number, searchTerm)
      )
    })
  }

  private searchByName(term: string, query: string): boolean {
    return term.toLowerCase().includes(query)
  }

  private searchByDocumentNumber(term: string, query: string): boolean {
    const documentNumber = query.replace(/[^0-9]/g, '')
    return !!documentNumber && term.includes(documentNumber)
  }

  public paginate(list: ClientList[], params: GetClientsArgs) {
    const { offset = 0, limit = DEFAULT_LIMIT } = params
    return list.slice(offset, offset + limit)
  }

  private filterByStatus(
    list: ClientList[],
    status: ClientListServiceStatus[]
  ) {
    return list.filter(({ terms_status, kyc_status }) => {
      const statusChecks = status.map((requiredStatus) => {
        switch (requiredStatus) {
          case ClientListServiceStatus.Register:
            return true
          case ClientListServiceStatus.Terms:
            return terms_status === ClientListTermsStatus.Accepted
          case ClientListServiceStatus.Credit:
            return kyc_status === ClientListKycStatus.Approved
          default:
            return false
        }
      })

      return statusChecks.every((check) => check === true)
    })
  }

  public filter(list: ClientList[], filters: GetClientsArgs) {
    const { status, search } = filters

    if (search && search.trim()) {
      return this.paginate(this.search(list, search.trim()), filters)
    }

    if (status) {
      return this.paginate(this.filterByStatus(list, status), filters)
    }

    return this.paginate(list, filters)
  }

  public async get(supplierId: string): Promise<ClientList[]> {
    return (await this.getData(supplierId)) ?? []
  }
}
